<script setup lang="ts">
import {ComponentPublicInstance, computed, inject, nextTick, reactive, ref} from "vue";
import Str from "../../../Utils/Str";
import User from "../../../Models/User";
import {saving} from "../../../Composables/api";
import {Avatar, Button, Dialog, Menu, Popover} from "../../Library";
import {Reactions} from "../index";
import type dayjs from "dayjs";
import type {Comment as CommentType} from "../../../Types";
import {deleteComment, storeComment, updateComment as apiUpdateComment} from "../../../Services/comments";
import {useMainStore} from "../../../Stores/store";
import {useRouter} from "vue-router";
import {useToast} from "primevue/usetoast";
import CommentForm from "./CommentForm.vue";
import axios from "axios";

const $store = useMainStore();
const $date = inject("date") as typeof dayjs;
const $user = new User();
const router = useRouter();
const toast = useToast();

const props = defineProps<{
    comment: CommentType;
    parent: any;
    allowReply: boolean;
    disabled?: boolean;
    allowVoice?: boolean;
    canAdmin?: boolean;
}>();
console.log(props);
const emit = defineEmits<{
    (e: "delete", id: number): void;
}>();

const commentRef = ref(props.comment);
computed({
    get: () => props.comment,
    set: (value) => {
        commentRef.value = value;
    }
});

const showReplyForm = ref(false);

const editText = ref(props.comment.original_text);

const state = reactive({
    show: {
        editComment: false,
        commentMenu: false
    }
});

const commentReply = ref();
const body = computed(() => {
    return Str.of(commentRef.value.text).urlify().value;
});
const commentTextarea = ref<ComponentPublicInstance | null>(null);
const editCommentTextarea = ref<ComponentPublicInstance | null>(null);
const commentMenu = ref<typeof Popover | null>(null);
const commentMenuItems = ref([
    {
        label: "Edit",
        icon: "prime:pencil",
        command: async () => {
            state.show.editComment = true;
            state.show.commentMenu = false;
            await nextTick();
            editCommentTextarea.value?.$el.focus();
        }
    },
    {
        label: "Delete",
        class: "text-red-600 hover:bg-red-100",
        icon: "prime:trash",
        command: async () => {
            console.log(props.comment);
            deleteComment(String(router.currentRoute.value.params.group), props.comment.id).then(() => {
                emit("delete", props.comment.id);
                state.show.commentMenu = false;
            });
        }
    }
]);

const showReply = async () => {
    showReplyForm.value = !showReplyForm.value;
    if (showReplyForm.value) {
        await nextTick();
        commentTextarea.value?.$el.focus();
    } else {
        commentReply.value = "";
    }
};

const hideReply = () => {
    showReplyForm.value = false;
    commentReply.value = "";
};

const postCommentReply = async (payload: {type: "text" | "voice"; data: string | File}) => {
    console.log(payload);
    let res;
    const {data} = await storeComment(router.currentRoute.value.params.group, "comment", props.comment.id, {
        parent: props.parent,
        comment: payload.type === "voice" ? "Voice message" : payload.data,
        group_id: router.currentRoute.value.params.group
    });
    res = data;

    if (payload.type === "voice") {
        const audioFormData = new FormData();
        audioFormData.append("files[]", payload.data, "audio.mp3");

        const {data: mediaData} = await axios.post(
            route("media.store", {
                modelType: "comment",
                modelId: res.id,
                collectionName: "audio"
            }),
            audioFormData,
            {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            }
        );

        res.files = mediaData.data;
    }

    if (!commentRef.value.replies) {
        commentRef.value.replies = [];
    }
    commentRef.value.replies.unshift(res);
    commentReply.value = null;
    showReplyForm.value = false;
};

const updateComment = async () => {
    try {
        let formData = {
            id: commentRef.value.id,
            comment: editText.value
        };
        const {data} = await apiUpdateComment(router.currentRoute.value.params.group, commentRef.value.id, formData);
        commentRef.value = data;
        editText.value = data.original_text;
    } catch (error) {
        console.log(error);
    } finally {
        state.show.editComment = false;
    }
};
</script>
<template>
    <div :id="`comment-${commentRef.id}`" :data-comment-id="commentRef.id">
        <div v-auto-animate>
            <!-- Comment header -->
            <div class="flex items-center">
                <div class="mb-2 flex flex-col items-center md:flex-row">
                    <Button @click="$store.show.user = commentRef.author?.id" class="!p-0 font-normal">
                        <Avatar :user="commentRef.author" class="mr-4 flex h-8 w-8 shrink-0 text-sm" shape="circle" />
                        <div class="flex flex-wrap items-center gap-0.5 text-stone-500 dark:text-stone-400">
                            <span class="text-sm font-bold">
                                {{ commentRef.author?.name }}
                            </span>
                            <div class="flex items-center pr-4">
                                <span class="mx-1 hidden lg:block">-</span>
                                <span class="text-xs">{{ $date(commentRef.updated_at) }}</span>
                                <span v-if="commentRef.approved_at !== commentRef.updated_at" class="ml-1 text-xs">
                                    (edited)
                                </span>
                            </div>
                        </div>
                    </Button>
                </div>
                <div
                    v-if="
                        $user.hasRole('super-admin') ||
                        canAdmin ||
                        (commentRef.author.id === $user.id && !state.show.editComment && !disabled)
                    "
                    class="ml-auto flex items-start justify-start p-1"
                    v-auto-animate
                >
                    <Button
                        @click="state.show.commentMenu = true"
                        :icon="{
                            icon: 'mdi:dots-vertical',
                            height: 24,
                        }"
                    />
                </div>
            </div>
            <div>
                <div v-auto-animate>
                    <!-- Editing comment -->
                    <div v-if="state.show.editComment" class="mb-3 pl-12">
                        <CommentForm
                            v-model="editText"
                            @submit="updateComment"
                            placeholder="Edit your comment"
                            :allowVoice="allowVoice"
                            :saving="saving"
                            @cancel="state.show.editComment = false"
                        />
                    </div>
                    <!-- Displaying comment -->
                    <div v-else>
                        <!-- Text comment -->
                        <div
                            v-if="!commentRef.files?.length"
                            class="comment-body prose-sm mb-3 break-words pl-12"
                            v-html="body"
                        ></div>

                        <!-- Voice comment -->
                        <audio
                            v-else-if="commentRef.files?.[0]?.url"
                            :src="commentRef.files[0].url"
                            controls
                            class="mb-3 ml-12 w-full max-w-md"
                        />

                        <!-- Bottom actions panel -->
                        <div class="flex items-end pl-12">
                            <Reactions
                                class="mr-auto"
                                :reactions="commentRef.reactions"
                                :reacted="commentRef.reacted"
                                :comment="commentRef"
                                :disabled="disabled"
                            />
                            <Button
                                v-if="props.allowReply && !disabled"
                                :icon="{
                                    icon: 'bi:reply-fill',
                                    width: 24,
                                }"
                                class="ml-auto"
                                @click="showReply(commentRef)"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <!-- Comment reply form -->
            <div v-if="allowReply && showReplyForm" class="mt-3 pl-12">
                <CommentForm
                    v-model="commentReply"
                    :placeholder="`Reply to ${commentRef.author?.name}`"
                    @submit="postCommentReply"
                    @cancel="hideReply"
                    :allowVoice="allowVoice"
                    :class="[{'mb-6': commentRef.replies?.length}]"
                    :saving="saving"
                />
            </div>
        </div>
        <!-- Comment actions menu -->
        <Dialog
            v-model:visible="state.show.commentMenu"
            modal
            dismissable-mask
            class="max-w-xs overflow-hidden"
            :closable="false"
            :show-header="false"
        >
            <template #container>
                <Menu ref="commentMenu" :model="commentMenuItems" />
            </template>
        </Dialog>
    </div>
</template>
<style scoped>
span.mention {
    text-decoration: underline;
}
</style>
